import {assert} from 'Asserts';
import {InventoryFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import {ClientId, FromServerId, ToServerId} from 'notebook/ELNConstants';
import {
  getErrorsForInvalidParentStorableCapacityInNonFinalizedRows,
  getParentStorablesByRowId,
} from 'notebook/integrations/samples/InventoryCapacityValidationFunctions';
import * as ParentStorableCustomEval from 'notebook/integrations/samples/transfer-table-columns/ParentStorableCustomEval';
import GenericStructuredTableAdapter from 'notebook/structured-tables/GenericStructuredTableAdapter';
import {
  BaseStructuredTable,
  CreateStructuredTableProps,
  StructuredTableColumn,
  StructuredTableMetadata,
  StructuredTableRow,
  TableValidationFn,
} from 'notebook/structured-tables/StructuredTableConstants';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import {CellIndex, CustomEval, RowMetadata, TableColumnType} from 'notebook/tables/TableConstants';

/**
 * A base adapter class for grid creation tables. Contains all logic shared between plate & box creation
 * tables, with additional specific logic contained in their respective adapters.
 */

/**
 * Keep in sync with benchling/notebook/structured_tables/constants/grid_creation_tables.py
 */
export interface GridCreationTableMetadata {
  // if false, then we will show a column where the barcode can be entered
  shouldAutogenerateBarcode: boolean;
  // If false, then we will show a column that is read-only with placeholder text. This property is missing in tables that were created before we offered the option for a custom name.
  requireCustomName?: boolean;
  // Should always be false. In fact, this property should be moved to the column:
  // TODO(BNCH-12025) Move to StructuredTableColumn
  parentStorableIsGrid: boolean;
}

/**
 * We don't have any need for any metadata on the row, so our TRowMetadata is just an empty
 * object. The typedef isn't necessary, but it's more explicit than "{}"
 */
export interface GridCreationTableRowMetadata extends RowMetadata {}

interface GridCreationTableColumnCustomEvalInfo {
  customEvalFn: CustomEval<{
    tableMetadata: StructuredTableMetadata & GridCreationTableMetadata;
    rowMetadata: GridCreationTableRowMetadata;
  }>;
  getColumnIndexesOfDependencies(columns: Array<StructuredTableColumn>): Array<number>;
}

// Keep in sync with benchling/notebook/structured_tables/instantiators/inventory_table_instantiator.py
export abstract class BaseGridCreationStructuredTableAdapter extends GenericStructuredTableAdapter<
  GridCreationTableMetadata,
  GridCreationTableRowMetadata
> {
  createTable(
    createProps: CreateStructuredTableProps<GridCreationTableMetadata>
  ): Omit<GridCreationStructuredTable, 'cid'> {
    assert(
      createProps.metadata.parentStorableIsGrid != null && !createProps.metadata.parentStorableIsGrid,
      'Grid creation table must have `parentStorableIsGrid` set to false.',
      {createProps}
    );
    return super.createTable(createProps);
  }

  private getColumnCustomEvalInfo(
    tableMetadata: GridCreationTableMetadata,
    column: StructuredTableColumn
  ): GridCreationTableColumnCustomEvalInfo | null {
    if (column.columnType === TableColumnType.PARENT_STORABLE) {
      return {
        customEvalFn: ParentStorableCustomEval.validateParentStorableCapacityCustomEval(),
        getColumnIndexesOfDependencies: (columns) => [],
      };
    }
    return null;
  }

  getTableSpecificCustomEvalFn(
    column: StructuredTableColumn,
    row: StructuredTableRow<GridCreationTableRowMetadata>,
    tableMetadata: GridCreationTableMetadata
  ): CustomEval<{
    tableMetadata: GridCreationTableMetadata;
    rowMetadata: GridCreationTableRowMetadata;
  }> | null {
    if (row.isFinalized) {
      return null;
    }

    return this.getColumnCustomEvalInfo(tableMetadata, column)?.customEvalFn ?? null;
  }

  getTableSpecificCustomEvalArgs(
    columns: Array<StructuredTableColumn>,
    currentColumnIndex: number,
    currentRowIndex: number,
    tableMetadata: GridCreationTableMetadata,
    toSpreadsheetIndex: (cellId: CellIndex) => string
  ): Array<string> {
    const dependencyColumnIndexes =
      this.getColumnCustomEvalInfo(
        tableMetadata,
        columns[currentColumnIndex]
      )?.getColumnIndexesOfDependencies(columns) ?? [];

    return dependencyColumnIndexes.map((dependencyIndex, slotIndex) => {
      assert(
        dependencyIndex !== -1,
        `Could not find dependency at slot ${slotIndex} for column at index ${currentColumnIndex}`
      );
      return `=${toSpreadsheetIndex({row: currentRowIndex, column: dependencyIndex})}`;
    });
  }

  getTableValidationFns(): Array<TableValidationFn<GridCreationTableMetadata, GridCreationTableRowMetadata>> {
    return [
      ...super.getTableValidationFns(),
      (structuredTable: GridCreationStructuredTable, computedTable: Array<Array<EvaluatedCell>>) => {
        const {parentStorablesByRowId, parentStorableColumnIndex} = getParentStorablesByRowId({
          structuredTable,
          computedTable,
        });
        return getErrorsForInvalidParentStorableCapacityInNonFinalizedRows({
          structuredTable,
          computedTable,
          parentStorablesByRowId,
          parentStorableColumnIndex,
        });
      },
    ];
  }

  /**
   * We don't need to extend the rows with any additional metadata, so
   * we always return an empty object here.
   */
  getBlankRowMetadata(tableMetadata: GridCreationTableMetadata): GridCreationTableRowMetadata {
    return {};
  }

  allowMergedCellRangeInColumn(
    structuredTable: GridCreationStructuredTable,
    column: StructuredTableColumn
  ): boolean {
    return ![TableColumnType.PLATE, TableColumnType.BOX, TableColumnType.STRING].includes(column.columnType);
  }

  isReadOnly(): boolean {
    return !isFeatureEnabled({feature: InventoryFeature.INVENTORY_CREATE});
  }
}

export type GenericGridCreationStructuredTable<TIds> = BaseStructuredTable<
  GridCreationTableMetadata,
  GridCreationTableRowMetadata,
  TIds
>;

export type GridCreationStructuredTable = GenericGridCreationStructuredTable<ClientId>;
export type FromServerPlateCreationStructuredTable = GenericGridCreationStructuredTable<FromServerId>;
export type ToServerPlateCreationStructuredTable = GenericGridCreationStructuredTable<ToServerId>;
